import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Activity,
  Box,
  Eye,
  EyeOff,
  History,
  Loader2,
  Plus,
  Rocket,
  RotateCcw,
  Save,
  Trash2,
  Wand2,
} from "lucide-react";

import { api, ApiError } from "@/api/client";
import { RegistryAuthIndicator } from "@/components/RegistryAuthIndicator";
import { useRollouts } from "@/api/local/rollout";
import {
  diffWorkloadSpec,
  draftRevisionOf,
  draftWorkloadOf,
  flattenArgs,
  hashWorkloadSpec,
  liveWorkloadOf,
  useDiscardDraftWorkload,
  useSaveDraftWorkload,
  type WorkloadArg,
  type WorkloadEnvVar,
  type WorkloadSpec,
} from "@/api/local/workload";
import type { Pool, Replica, ReplicasDesired, Service } from "@/api/types";
import { Pill } from "@/components/Pill";
import { RolloutModal } from "@/components/RolloutModal";
import { Section } from "@/components/Section";
import { computeTargetReplicas } from "@/pages/workloads/pool/PoolReplicas";

interface Props {
  service: Service;
  /** Pools under this service, used to build the per-pool rollout plan. */
  pools: Pool[];
  /**
   * Live replicas under this service. Currently unused (the rollout
   * plan reads `replicas_desired` instead so the count reflects what
   * the reconciler will converge on, not what is alive right now), but
   * kept on the prop shape so the caller doesn't need to be updated
   * along with this file.
   */
  replicas: Replica[];
}

/**
 * Workload definition page.
 *
 * Both *live* and *draft* specs live on the controller now
 * (`Service.workload` / `Service.workload_draft`). The editor binds to
 * the draft revision; clicking "Save draft" PUTs to the controller,
 * "Discard" POSTs the discard endpoint, and "Roll out" promotes
 * draft → live (then animates the blue/green ceremony).
 *
 * Local component state holds the *unsaved* edits between keystrokes
 * and the next Save click — that's the only client-side buffer.
 */
export function ServiceWorkload({ service, pools }: Props) {
  const rollouts = useRollouts();
  const queryClient = useQueryClient();

  const live = useMemo(() => liveWorkloadOf(service), [service]);
  const persistedDraft = useMemo(() => draftWorkloadOf(service), [service]);
  const draftService = service.lifecycle_state === "draft";

  // Local edit buffer. Re-seeded whenever the persisted draft changes
  // server-side (e.g. teammate saved on another tab) or whenever the
  // optimistic-lock revision moves on (we may end up seeded with the
  // identical spec but at a higher revision after a discard/promote).
  const [draft, setDraft] = useState<WorkloadSpec>(persistedDraft);
  const [pristineHash, setPristineHash] = useState(() =>
    hashWorkloadSpec(persistedDraft),
  );
  // Revision the editor was seeded from. Echoed back via If-Match on
  // save; if it doesn't match what the server has (because a teammate
  // or rollout moved the revision), the PUT 412s and we surface the
  // conflict banner below.
  const [pristineRevision, setPristineRevision] = useState(() =>
    draftRevisionOf(service),
  );
  const [conflict, setConflict] = useState(false);
  const dirty = hashWorkloadSpec(draft) !== pristineHash;
  useEffect(() => {
    // A background refetch (the topbar auto-refresh timer, a teammate's
    // save, a rollout completing, a lifecycle flip) moves
    // `persistedDraft` / `service` and re-fires this effect. Do NOT let
    // that clobber the operator's in-progress edits or tear down a live
    // conflict banner: only reseed the local buffer from the server when
    // the editor is clean AND no conflict is being resolved. `dirty` /
    // `conflict` are read from the current render's closure on purpose
    // (they are intentionally not deps — see the reseed-on-save handlers
    // below, which clear `dirty` so the next refetch reseeds cleanly).
    if (dirty || conflict) return;
    setDraft(persistedDraft);
    setPristineHash(hashWorkloadSpec(persistedDraft));
    setPristineRevision(draftRevisionOf(service));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedDraft, service]);

  const [showRollout, setShowRollout] = useState(false);

  const saveMutation = useSaveDraftWorkload(service.id);
  const discardMutation = useDiscardDraftWorkload(service.id);

  // Note: progress for an active rollout is rendered inline on each
  // affected pool's Replicas tab via `PoolRolloutPanel`. We deliberately
  // do *not* auto-open the modal here — that was the legacy behaviour
  // and led to a full-screen progress modal popping up the moment the
  // operator hit "Roll out". The modal remains available on demand via
  // the "view rollout" button below.

  // Pending diff = persisted draft vs. live (NOT the local edit
  // buffer — pending changes only count once they're saved).
  const pendingDiffs = useMemo(
    () => diffWorkloadSpec(live, persistedDraft),
    [live, persistedDraft],
  );

  const save = () => {
    setConflict(false);
    saveMutation.mutate(
      { spec: draft, ifRevision: pristineRevision },
      {
        onSuccess: () => {
          // Mark the just-saved buffer as the new pristine baseline so
          // `dirty` clears immediately; the revision is refreshed from
          // the server by the reseed effect once the invalidated
          // `services` query refetches.
          setPristineHash(hashWorkloadSpec(draft));
          setConflict(false);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 412) {
            setConflict(true);
            // The freshest data will arrive via the services
            // invalidation in the mutation's onSuccess sibling — when
            // it lands, the reseed effect keeps the buffer + banner
            // (guarded by `conflict`) instead of overwriting them.
            queryClient.invalidateQueries({ queryKey: ["services"] });
          }
        },
      },
    );
  };

  const discard = () => {
    discardMutation.mutate(undefined, {
      onSuccess: () => {
        setDraft(live);
        setPristineHash(hashWorkloadSpec(live));
        setConflict(false);
      },
    });
  };

  const reseedFromServer = () => {
    setDraft(persistedDraft);
    setPristineHash(hashWorkloadSpec(persistedDraft));
    setPristineRevision(draftRevisionOf(service));
    setConflict(false);
  };

  // Per-pool desired counts so we can show the *target* replica count
  // (what the reconciler will converge on) in the rollout plan rather
  // than the count of live replicas. Live count is misleading on first
  // deploy (always 0) and during scale-up. The desired endpoint returns
  // one row per pool with a manual/schedule/autoscaler row; missing
  // rows fall back to `pool.min_replicas` via `computeTargetReplicas`.
  const desiredQuery = useQuery({
    queryKey: ["service", service.id, "desired"],
    queryFn: () =>
      api.get<ReplicasDesired[]>(`/services/${service.id}/desired`),
  });

  // Build the per-pool rollout plan from current data. We use the
  // *persisted* draft as the rollout target — unsaved local edits do
  // not get rolled.
  const plan = useMemo(
    () =>
      pools.map((p) => ({
        pool_id: p.id,
        pool_name: p.sky_service_name,
        replicas: computeTargetReplicas(
          p,
          (desiredQuery.data ?? []).find((r) => r.pool_id === p.id) ?? null,
        ),
        target: persistedDraft,
      })),
    [pools, desiredQuery.data, persistedDraft],
  );

  const isRolloutActive =
    !!rollouts.active && rollouts.active.service_id === service.id;
  const noPools = pools.length === 0;
  // Live considered "empty" when there's no image — that's the
  // pre-first-deploy state.
  const liveEmpty = !live.image;
  // Pool count is *not* a gate: a service-scoped rollout with zero
  // pools is a valid "promote workload + flip lifecycle now, attach
  // capacity later" workflow. The controller short-circuits to
  // ``completed`` on the next engine tick. We still gate on a
  // saved-and-dirty-free draft so the operator doesn't roll a half-
  // typed spec, and on no other rollout being in flight.
  const canRollout =
    !isRolloutActive &&
    !dirty &&
    (draftService || liveEmpty || pendingDiffs.length > 0);

  return (
    <Section
      title="What to deploy"
      icon={<Box />}
      count={
        <span style={{ display: "inline-flex", gap: 6 }}>
          <Pill tone="brand">docker</Pill>
          <Pill tone={noPools ? "warn" : "neutral"}>
            {pools.length} pool{pools.length === 1 ? "" : "s"}
          </Pill>
          {draftService && <Pill tone="info">draft</Pill>}
          {pendingDiffs.length > 0 && !draftService && (
            <Pill tone="warn">
              {pendingDiffs.length} pending change
              {pendingDiffs.length === 1 ? "" : "s"}
            </Pill>
          )}
          {liveEmpty && !draftService && (
            <Pill tone="info">never rolled out</Pill>
          )}
          {isRolloutActive && rollouts.active && (
            <Pill tone="info" pulse>
              rollout {rollouts.active.state}
            </Pill>
          )}
        </span>
      }
      actions={
        <div className="toolbar" style={{ gap: 6 }}>
          <button
            className="btn btn--ghost"
            onClick={discard}
            disabled={
              discardMutation.isPending ||
              (pendingDiffs.length === 0 && !dirty)
            }
            title="Reset draft to live spec"
          >
            {discardMutation.isPending ? <Loader2 /> : <RotateCcw />}
            discard
          </button>
          <button
            className="btn"
            onClick={save}
            disabled={!dirty || saveMutation.isPending}
          >
            {saveMutation.isPending ? <Loader2 /> : <Save />}
            {saveMutation.isPending
              ? "saving…"
              : dirty
                ? "save draft"
                : "saved"}
          </button>
          <button
            className="btn btn--primary"
            disabled={!canRollout && !isRolloutActive}
            onClick={() => setShowRollout(true)}
            title={
              dirty
                ? "Save your draft before rolling out"
                : noPools
                  ? "No pools yet — rollout will lock in the workload now; add capacity afterwards"
                  : undefined
            }
          >
            <Rocket />
            {isRolloutActive
              ? "view rollout"
              : draftService
                ? "First deploy…"
                : pendingDiffs.length === 0 && !liveEmpty
                  ? "in sync"
                  : "Roll out…"}
          </button>
        </div>
      }
    >
      {draftService && (
        <div className="flash flash--info" style={{ marginBottom: 16 }}>
          <AlertTriangle
            style={{
              width: 14,
              height: 14,
              verticalAlign: "middle",
              marginRight: 6,
            }}
          />
          This service is a <strong>draft</strong> — it&rsquo;s parked on
          the controller (<code>enabled: false</code>) until you confirm
          the first rollout. Configure the workload spec below, then
          click <strong>First deploy…</strong> to lock it in. Pools (the
          where) can be attached before <em>or</em> after first deploy
          via the <strong>+ pool</strong> button on this service&rsquo;s
          row in the tree above — once a pool has{" "}
          <code>max_replicas &gt; 0</code> the reconciler picks it up
          using the workload you just promoted.
        </div>
      )}

      {dirty && (
        <div className="flash" style={{ marginBottom: 16 }}>
          You have unsaved edits. Click <strong>save draft</strong> to
          persist them on the controller, or <strong>discard</strong> to
          revert.
        </div>
      )}

      {conflict && (
        <div className="flash flash--warn" style={{ marginBottom: 16 }}>
          <AlertTriangle
            style={{
              width: 14,
              height: 14,
              verticalAlign: "middle",
              marginRight: 6,
            }}
          />
          The draft was modified somewhere else (a teammate saved, or a
          rollout promoted it) while you were editing. Your unsaved edits
          are still in this tab — review the latest draft from the
          server and either{" "}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={reseedFromServer}
            style={{ display: "inline-flex" }}
          >
            <RotateCcw />
            load latest
          </button>{" "}
          (discards your edits) or{" "}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => {
              // Force-save: drop the If-Match guard so the next PUT
              // wins regardless of revision. Saves the LOCAL buffer
              // (`draft` — "mine"), not the server copy. Use sparingly —
              // this overwrites whatever the other party saved.
              setConflict(false);
              saveMutation.mutate(
                { spec: draft },
                {
                  onSuccess: () => {
                    setPristineHash(hashWorkloadSpec(draft));
                    setConflict(false);
                  },
                },
              );
            }}
            style={{ display: "inline-flex" }}
          >
            <Save />
            force-save mine
          </button>
          .
        </div>
      )}

      {saveMutation.isError && !conflict && (
        <div className="flash flash--info" style={{ marginBottom: 16 }}>
          <AlertTriangle
            style={{
              width: 14,
              height: 14,
              verticalAlign: "middle",
              marginRight: 6,
            }}
          />
          Save failed:{" "}
          {saveMutation.error instanceof Error
            ? saveMutation.error.message
            : "unknown error"}
          . Your edits are still in this tab — fix the issue and retry.
        </div>
      )}

      <DockerEditor value={draft} onChange={setDraft} />

      <p className="muted" style={{ fontSize: 11.5, marginTop: 16 }}>
        Workloads are docker images — bring any inference server you like,
        or hand BackStage a vLLM image with <code>--model &lt;repo&gt;</code>{" "}
        to serve a Hugging Face model. Saving only updates the draft;
        replicas keep running on the live spec until you launch a rollout,
        which promotes draft → live and walks the replicas through
        blue/green. Pools (where it runs) live on the tree above — use{" "}
        <strong>+ pool</strong> on this service&rsquo;s row to attach
        capacity.
      </p>

      {rollouts.history.length > 0 && (
        <div className="muted" style={{ fontSize: 11.5, marginTop: 16 }}>
          <History
            style={{
              width: 12,
              height: 12,
              verticalAlign: "middle",
              marginRight: 4,
            }}
          />
          {rollouts.history.length} past rollout
          {rollouts.history.length === 1 ? "" : "s"} on record — see the
          BackStage <strong>Rollouts</strong> tab for the full history and
          per-pool diff.
        </div>
      )}

      <RolloutModal
        open={showRollout}
        onClose={() => setShowRollout(false)}
        scope="service"
        scopeLabel={service.name}
        serviceId={service.id}
        source={liveEmpty ? null : live}
        target={persistedDraft}
        plan={plan}
        firstDeploy={draftService}
        onComplete={
          draftService
            ? async () => {
                // First-deploy success: ask the controller to promote
                // the row to active. The lifecycle endpoint flips
                // `enabled=true` + `lifecycle_state=active` atomically.
                try {
                  await api.patch(`/services/${service.id}/lifecycle`, {
                    lifecycle_state: "active",
                  });
                } finally {
                  queryClient.invalidateQueries({ queryKey: ["services"] });
                }
              }
            : undefined
        }
      />
    </Section>
  );
}

interface DockerEditorProps {
  value: WorkloadSpec;
  onChange: (next: WorkloadSpec) => void;
}

function DockerEditor({ value, onChange }: DockerEditorProps) {
  return (
    <>
      <SubBlock title="Image" icon={<Box />}>
        <div className="form-grid">
          <label>image</label>
          <div className="field">
            <input
              value={value.image ?? ""}
              onChange={(e) =>
                onChange({
                  ...value,
                  image: e.target.value === "" ? null : e.target.value,
                })
              }
              placeholder="ghcr.io/spiral-ai/backstage-loadtest:latest"
            />
            <RegistryAuthIndicator image={value.image} />
            <div className="field-hint">
              Defaults to{" "}
              <code>ghcr.io/spiral-ai/backstage-loadtest</code> — the
              backstage-published load-testing target with{" "}
              <code>/healthz</code>, <code>/metrics</code>, a WAV /
              chunked / WebSocket surface, and a GPU emulator. Swap to
              any inference image (vLLM, TGI, custom). Private
              registries need a matching credential — the pill above
              tells you whether one is configured for this image's
              host.
            </div>
            <div
              className="field-hint"
              style={{ color: "var(--color-warn, #b25b00)" }}
            >
              <strong>Requirements:</strong> the image MUST include{" "}
              <code>sshd</code> (openssh-server) <em>and</em> a POSIX{" "}
              shell (<code>/bin/sh</code> or <code>bash</code>).
              SkyPilot opens an SSH session into the running container
              to install its runtime and exec the{" "}
              <code>setup</code>/<code>run</code> scripts; minimal
              images (Alpine + node only, scratch, distroless, …) fail
              with <em>“Failed to SSH to …”</em>. RunPod is especially
              strict because its pod-is-a-container model has SkyPilot
              SSHing into the workload image directly, not a host VM.
              Safe base images:{" "}
              <code>runpod/base:1.0.3-ubuntu2404</code>, anything
              FROM <code>ubuntu:22.04</code> + an{" "}
              <code>openssh-server</code> install. Pin a fully-
              qualified tag — RunPod scrubs old series (the{" "}
              <code>0.6.x</code> family no longer resolves and a
              stale recommendation lands you in a{" "}
              <code>manifest unknown</code> container-create failure.
              On save the controller probes the registry and rejects
              a missing tag up-front; if a tag is yanked while in
              flight the periodic sweep re-flags it.
            </div>
          </div>
          <label>command</label>
          <div className="field">
            <input
              value={value.command ?? ""}
              onChange={(e) =>
                onChange({
                  ...value,
                  command: e.target.value === "" ? null : e.target.value,
                })
              }
              placeholder="/usr/local/bin/loadtest-server"
            />
            <div className="field-hint">
              Entrypoint command line. SkyPilot&rsquo;s docker runtime
              bypasses the image&rsquo;s built-in <code>CMD</code>, so
              leave this empty only if you want the container to{" "}
              <em>sleep forever</em>. The default loadtest image ships
              its entrypoint at{" "}
              <code>/usr/local/bin/loadtest-server</code>; vLLM
              workloads typically run{" "}
              <code>python -m vllm.entrypoints.openai.api_server</code>.
            </div>
          </div>
          <label>port</label>
          <div className="field">
            <input
              type="number"
              value={value.port}
              onChange={(e) =>
                onChange({ ...value, port: Number(e.target.value) })
              }
            />
            <div className="field-hint">
              Health-checks and Envoy targets bind to this port on each
              replica.
            </div>
          </div>
        </div>
      </SubBlock>

      <SubBlock title="Args" icon={<Wand2 />}>
        <ArgsEditor
          args={value.args}
          onChange={(next) => onChange({ ...value, args: next })}
          flagPlaceholder="--model"
          valuePlaceholder="meta-llama/Llama-3-70B-Instruct"
        />
        <p className="muted" style={{ fontSize: 11, marginTop: 8 }}>
          One row per <code>--flag value</code>. Boolean flags (e.g.{" "}
          <code>--enforce-eager</code>) leave the value empty. For{" "}
          positional args, fill the flag column and leave value empty.
        </p>
      </SubBlock>

      <SubBlock title="Env" icon={<Box />}>
        <EnvEditor
          env={value.env}
          onChange={(next) => onChange({ ...value, env: next })}
        />
        <p className="muted" style={{ fontSize: 11, marginTop: 8 }}>
          For pre-quantized HF models (e.g. <code>TheBloke/...-AWQ</code>):
          point <code>--model</code> at the quantized repo and add{" "}
          <code>--quantization awq</code> as an arg. BackStage doesn&rsquo;t
          quantize on its own — that&rsquo;s a hint for the loader.
        </p>
      </SubBlock>

      <SubBlock title="Probe" icon={<Activity />}>
        <div className="form-grid">
          <label>health path</label>
          <div className="field">
            <input
              value={value.health_path}
              onChange={(e) =>
                onChange({ ...value, health_path: e.target.value })
              }
              placeholder="/healthz"
            />
            <div className="field-hint">
              Forwarded to SkyPilot&rsquo;s readiness probe. Polled on{" "}
              <code>http://&lt;replica&gt;:&lt;port&gt;{value.health_path}</code>.
            </div>
          </div>
          <label>initial delay (s)</label>
          <div className="field">
            <input
              type="number"
              min={0}
              value={value.initial_delay_seconds}
              onChange={(e) =>
                onChange({
                  ...value,
                  initial_delay_seconds: Number(e.target.value),
                })
              }
              style={{ width: 120 }}
            />
            <div className="field-hint">
              Grace period after a replica boots before SkyPilot starts
              polling the health probe. Bump this for slow-loading
              models.
            </div>
          </div>
          <label>metrics path</label>
          <div className="field">
            <input
              value={value.metrics_path}
              onChange={(e) =>
                onChange({ ...value, metrics_path: e.target.value })
              }
              placeholder="/metrics"
            />
            <div className="field-hint">
              Prometheus exposition path. The port is shared with{" "}
              <code>port</code> by convention, so the scrape target is{" "}
              <code>
                http://&lt;replica&gt;:&lt;port&gt;{value.metrics_path}
              </code>
              . Forwarded into the rendered SkyPilot service.yaml as{" "}
              <code>METRICS_PATH</code> for downstream scrapers.
            </div>
          </div>
        </div>
      </SubBlock>

      <ArgvPreview workload={value} />
    </>
  );
}

function SubBlock({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="subblock">
      <div className="subblock__title">
        {icon}
        <span>{title}</span>
      </div>
      <div className="subblock__body">{children}</div>
    </div>
  );
}

interface ArgsEditorProps {
  args: WorkloadArg[];
  onChange: (next: WorkloadArg[]) => void;
  flagPlaceholder?: string;
  valuePlaceholder?: string;
}

function ArgsEditor({
  args,
  onChange,
  flagPlaceholder,
  valuePlaceholder,
}: ArgsEditorProps) {
  const update = (idx: number, patch: Partial<WorkloadArg>) => {
    const next = args.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  return (
    <div className="argrows">
      {args.map((a, idx) => (
        <div key={idx} className="argrow">
          <input
            className="argrow__flag"
            value={a.flag}
            placeholder={flagPlaceholder ?? "--flag"}
            onChange={(e) => update(idx, { flag: e.target.value })}
          />
          <input
            className="argrow__value"
            value={a.value ?? ""}
            placeholder={valuePlaceholder ?? "value (optional)"}
            onChange={(e) => update(idx, { value: e.target.value })}
          />
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => onChange(args.filter((_, i) => i !== idx))}
            aria-label="remove"
          >
            <Trash2 />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() => onChange([...args, { flag: "", value: "" }])}
      >
        <Plus />
        add arg
      </button>
    </div>
  );
}

function EnvEditor({
  env,
  onChange,
}: {
  env: WorkloadEnvVar[];
  onChange: (next: WorkloadEnvVar[]) => void;
}) {
  const [revealed, setRevealed] = useState<Set<number>>(new Set());

  const toggleReveal = (idx: number) => {
    const next = new Set(revealed);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setRevealed(next);
  };

  return (
    <div className="argrows">
      {env.map((e, idx) => {
        const isSecret = !!e.secret;
        const show = !isSecret || revealed.has(idx);
        return (
          <div key={idx} className="argrow">
            <input
              className="argrow__flag"
              value={e.key}
              placeholder="KEY"
              onChange={(ev) => {
                const next = env.slice();
                next[idx] = { ...e, key: ev.target.value };
                onChange(next);
              }}
            />
            <input
              className="argrow__value"
              type={show ? "text" : "password"}
              value={e.value}
              placeholder="value"
              onChange={(ev) => {
                const next = env.slice();
                next[idx] = { ...e, value: ev.target.value };
                onChange(next);
              }}
            />
            <label
              className="muted"
              style={{
                display: "inline-flex",
                gap: 4,
                alignItems: "center",
                fontSize: 10.5,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
              }}
            >
              <input
                type="checkbox"
                checked={isSecret}
                onChange={(ev) => {
                  const next = env.slice();
                  next[idx] = { ...e, secret: ev.target.checked };
                  onChange(next);
                }}
              />
              secret
            </label>
            {isSecret && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => toggleReveal(idx)}
                aria-label="reveal"
              >
                {show ? <EyeOff /> : <Eye />}
              </button>
            )}
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => onChange(env.filter((_, i) => i !== idx))}
              aria-label="remove"
            >
              <Trash2 />
            </button>
          </div>
        );
      })}
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() => onChange([...env, { key: "", value: "" }])}
      >
        <Plus />
        add env var
      </button>
    </div>
  );
}

function ArgvPreview({ workload }: { workload: WorkloadSpec }) {
  const argv: string[] = [];
  if (workload.command) argv.push(workload.command);
  argv.push(...flattenArgs(workload.args));
  return (
    <div className="subblock">
      <div className="subblock__title">
        <Wand2 />
        <span>Rendered argv</span>
      </div>
      <pre className="code" style={{ marginTop: 0 }}>
        {workload.image ?? "(no image)"}
        {"\n  "}
        {argv.length === 0 ? "(no args)" : argv.join(" ")}
      </pre>
      <p className="muted" style={{ fontSize: 11, marginTop: 8 }}>
        Preview of what the controller will hand to SkyServe on every
        replica. Args are appended to the command verbatim.
      </p>
    </div>
  );
}
